import { useState } from "react";
import { ArrowRight, Heart, LoaderCircle, Mail, Phone } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { CONTACT } from "@/data/content";
import { trackLeadConversion, trackMicroConversion, trackFormStarted } from "@/lib/tracking";

const inputCls = (hasError) =>
  `w-full border ${hasError ? "border-rust" : "border-sand"} bg-creme px-5 py-4 text-base text-ink placeholder:text-stone/50 outline-none transition-colors focus:border-moss`;

const labelCls = "mb-2 block text-xs font-semibold uppercase tracking-[0.2em] text-rust";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

const FieldError = ({ id, msg }) =>
  msg ? (
    <p id={id} data-testid={id} className="mt-2 text-sm font-medium text-rust">
      {msg}
    </p>
  ) : null;

const ContactForm = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: "",
    email: "",
    date: "",
    phone: "",
    location: "",
    message: "",
    contactPreference: "",
    website: "", // Honeypot – bleibt für Menschen unsichtbar
  });
  const [errors, setErrors] = useState({});
  const [state, setState] = useState("idle");

  const set = (k) => (e) => {
    trackFormStarted();
    setForm({ ...form, [k]: e.target.value });
    if (errors[k]) setErrors({ ...errors, [k]: undefined });
  };

  const validate = () => {
    const errs = {};
    if (form.name.trim().length < 2) errs.name = "Bitte gebt euren Namen ein.";
    if (!form.email.trim()) errs.email = "Bitte gebt eure E-Mail-Adresse ein.";
    else if (!EMAIL_RE.test(form.email.trim())) errs.email = "Bitte überprüft eure E-Mail-Adresse.";
    if (!form.date) errs.date = "Bitte wählt euren Hochzeitstermin aus.";
    return errs;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (state === "sending") return;
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setState("sending");
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name.trim(),
          email: form.email.trim(),
          date: form.date,
          phone: form.phone.trim() || null,
          location: form.location.trim() || null,
          message: form.message.trim() || null,
          contact_preference: form.contactPreference || null,
          website: form.website,
        }),
      });
      if (!res.ok) throw new Error();
      // Primäre Conversion: genau einmal, nur bei tatsächlich erfolgreichem Versand
      trackLeadConversion();
      navigate("/danke-anfrage");
    } catch {
      setState("error");
    }
  };

  return (
    <form onSubmit={submit} noValidate data-testid="contact-form" className="space-y-5">
      <p
        data-testid="contact-trust-note"
        className="flex items-start gap-3 border border-sand bg-parch/60 px-5 py-4 text-sm leading-relaxed text-stone"
      >
        <Heart size={16} className="mt-0.5 shrink-0 text-rust" />
        Eure Anfrage ist unverbindlich. Wir melden uns persönlich bei euch.
      </p>

      <div className="grid gap-5 sm:grid-cols-2">
        <div>
          <label htmlFor="cf-name" className={labelCls}>Euer Name *</label>
          <input
            id="cf-name"
            data-testid="contact-name"
            autoComplete="name"
            value={form.name}
            onChange={set("name")}
            aria-invalid={!!errors.name}
            aria-describedby={errors.name ? "contact-name-error" : undefined}
            placeholder="Anna & Tom"
            className={inputCls(!!errors.name)}
          />
          <FieldError id="contact-name-error" msg={errors.name} />
        </div>
        <div>
          <label htmlFor="cf-email" className={labelCls}>E-Mail-Adresse *</label>
          <input
            id="cf-email"
            data-testid="contact-email"
            type="email"
            inputMode="email"
            autoComplete="email"
            value={form.email}
            onChange={set("email")}
            aria-invalid={!!errors.email}
            aria-describedby={errors.email ? "contact-email-error" : undefined}
            placeholder="ihr@beispiel.de"
            className={inputCls(!!errors.email)}
          />
          <FieldError id="contact-email-error" msg={errors.email} />
        </div>
      </div>

      <div className="grid gap-5 sm:grid-cols-2">
        <div>
          <label htmlFor="cf-date" className={labelCls}>Euer Hochzeitstermin *</label>
          <input
            id="cf-date"
            data-testid="contact-date"
            type="date"
            min={new Date().toISOString().split("T")[0]}
            value={form.date}
            onChange={set("date")}
            aria-invalid={!!errors.date}
            aria-describedby={errors.date ? "contact-date-error" : undefined}
            className={inputCls(!!errors.date)}
          />
          <FieldError id="contact-date-error" msg={errors.date} />
        </div>
        <div>
          <label htmlFor="cf-phone" className={labelCls}>Telefonnummer</label>
          <input
            id="cf-phone"
            data-testid="contact-phone"
            type="tel"
            inputMode="tel"
            autoComplete="tel"
            value={form.phone}
            onChange={set("phone")}
            placeholder="Für schnelle Rückfragen"
            className={inputCls(false)}
          />
        </div>
      </div>

      <div>
        <label htmlFor="cf-location" className={labelCls}>Ort / Hochzeitslocation</label>
        <input
          id="cf-location"
          data-testid="contact-location"
          value={form.location}
          onChange={set("location")}
          placeholder="z. B. Garten in Krefeld, Gutshof am Niederrhein …"
          className={inputCls(false)}
        />
      </div>

      <fieldset>
        <legend className={labelCls}>Wie dürfen wir euch am liebsten kontaktieren?</legend>
        <div className="flex flex-wrap gap-3">
          {[
            ["email", "E-Mail"],
            ["phone", "Telefon"],
          ].map(([value, label]) => (
            <label
              key={value}
              className={`flex cursor-pointer items-center gap-2 border px-5 py-3 text-sm font-semibold transition-colors duration-300 ${
                form.contactPreference === value
                  ? "border-moss bg-moss/10 text-mossdeep"
                  : "border-sand bg-creme text-stone hover:border-moss/50"
              }`}
            >
              <input
                type="radio"
                name="contact-preference"
                data-testid={`contact-pref-${value}`}
                value={value}
                checked={form.contactPreference === value}
                onChange={set("contactPreference")}
                className="accent-moss"
              />
              {label}
            </label>
          ))}
        </div>
      </fieldset>

      <div>
        <label htmlFor="cf-message" className={labelCls}>Eure Nachricht</label>
        <textarea
          id="cf-message"
          data-testid="contact-message"
          rows={4}
          value={form.message}
          onChange={set("message")}
          placeholder="Wie habt ihr euch kennengelernt? Was wünscht ihr euch für eure Trauung?"
          className={`${inputCls(false)} resize-y`}
        />
      </div>

      {/* Honeypot: für Menschen unsichtbar, Bots füllen es aus */}
      <div className="hidden" aria-hidden="true">
        <label htmlFor="cf-website">Website</label>
        <input
          id="cf-website"
          name="website"
          tabIndex={-1}
          autoComplete="off"
          value={form.website}
          onChange={set("website")}
        />
      </div>

      {state === "error" && (
        <p
          data-testid="contact-error"
          className="border border-rust/40 bg-rust/10 px-5 py-4 text-sm text-rust"
        >
          Das hat leider nicht geklappt. Versucht es gern in ein paar Minuten noch einmal –
          oder ruft uns direkt an: {CONTACT.phone}
        </p>
      )}

      <div>
        <button
          type="submit"
          data-testid="contact-submit"
          disabled={state === "sending"}
          className="flex w-full items-center justify-center gap-3 rounded-full bg-moss px-9 py-5 text-base font-bold text-creme transition-all duration-300 hover:bg-mossdeep disabled:opacity-60"
        >
          {state === "sending" ? (
            <>
              <LoaderCircle size={18} className="animate-spin" /> Wird gesendet …
            </>
          ) : (
            <>
              Kennenlerngespräch anfragen <ArrowRight size={17} />
            </>
          )}
        </button>
        <p
          data-testid="contact-submit-note"
          className="mt-3 text-center text-xs font-semibold uppercase tracking-[0.22em] text-stone"
        >
          Kostenlos · unverbindlich · persönliche Rückmeldung
        </p>
      </div>

      <p data-testid="contact-privacy-note" className="text-center text-xs leading-relaxed text-stone">
        Mit dem Absenden stimmt ihr der Verarbeitung eurer Angaben zur Bearbeitung eurer
        Anfrage zu. Alle Details findet ihr in unserer{" "}
        <Link
          to="/datenschutz"
          className="font-semibold text-ink underline decoration-sand underline-offset-2 hover:text-moss"
        >
          Datenschutzerklärung
        </Link>
        .
      </p>

      <div className="flex flex-wrap items-center justify-center gap-x-8 gap-y-2 pt-2 text-sm">
        <a
          href={CONTACT.phoneHref}
          data-testid="contact-phone-link"
          onClick={() => trackMicroConversion("phone")}
          className="inline-flex items-center gap-2 font-semibold text-moss"
        >
          <Phone size={15} /> {CONTACT.phone}
        </a>
        <a
          href={`mailto:${CONTACT.email}`}
          data-testid="contact-mail-link"
          onClick={() => trackMicroConversion("email")}
          className="inline-flex items-center gap-2 font-semibold text-moss"
        >
          <Mail size={15} /> {CONTACT.email}
        </a>
      </div>
    </form>
  );
};

export default ContactForm;
